import { urlParameterCodes } from '@/constants/urlParams';
import type { SelectedProductVariants } from '@/ui/components/pages/ProductDetail/types/SelectedProductVariants';
import {
  getPathWithoutLocalePrefix,
  removePathPrefix,
} from '@/ui/utils/localization-helpers/localization';
import { formatBundleSkusParam } from '@/utils/bundle-helper';
import type { BundleProduct } from '../BundleProductService';
import type { Bundle } from '../BundleService';

const { productDetail } = urlParameterCodes;

/**
 * The SKUs a link carries for the given slots, shared by the links and the selection hook's
 * URL. A link restores the defaults on its own, so the short form keeps only the choices that
 * differ from the default and keeps the common URL clean. When two selectable slots share a
 * product and one of them is off its default, every selectable slot is carried in slot order,
 * defaults included: matching by position is the only way to tell those slots apart.
 */
export const getOffDefaultSkus = (
  products: BundleProduct[],
  selected: SelectedProductVariants | undefined,
): string[] => {
  const selectable = products.filter((product) => product.isSelectable());
  const skus = selectable.map((product) => product.resolveVariant(selected).sku);
  const offDefault = skus.filter((sku, i) => sku !== selectable[i].defaultVariant.sku);
  const sharesProduct = new Set(selectable.map((product) => product.id)).size < selectable.length;

  return sharesProduct && offDefault.length > 0 ? skus : offDefault;
};

const withQuery = (path: string, params: URLSearchParams): string => {
  const query = params.toString();
  if (!query) {
    return path;
  }

  return `${path}${path.includes('?') ? '&' : '?'}${query}`;
};

// The parameters that open the chooser of the bundle's Smart Lock with the bundle preselected.
export function buildChooserParams(bundle: Bundle): URLSearchParams {
  const params = new URLSearchParams();

  params.set(productDetail.chooserOpen, 'true');

  if (bundle.documentExtRef) {
    params.set(productDetail.bundleId, bundle.documentExtRef);
  }

  if (bundle.hasSubscription) {
    params.set(productDetail.subscription, 'true');
  }

  // The upgrade program param is what triggers the device check modal in
  // SmartlockDetailOverlay, which an upgrade bundle must pass before it sells.
  if (bundle.documentExtRef && bundle.isUpgradeBundle) {
    params.set(productDetail.upgrade, bundle.documentExtRef);
  }

  return params;
}

// A CTA on the bundle's own page cannot link to the page, so it has to recognise that page
// behind the CMS hierarchy prefix and the visitor's locale prefix.
export function isBundlePagePath(bundle: Bundle, pathname: string): boolean {
  const pagePath = bundle.getLinkablePagePath();
  if (!pagePath) {
    return false;
  }

  return removePathPrefix(pagePath) === `/${getPathWithoutLocalePrefix(pathname)}`;
}

/**
 * The chooser link of the bundle with the given variants preselected. The Smart Lock is the
 * host and keeps its own `sku` parameter, so `setSkus` names the other slots only.
 */
export function buildChooserPathForSelection(
  bundle: Bundle,
  selected: SelectedProductVariants | undefined,
): string | undefined {
  const smartlock = bundle.getSmartlock();
  if (!smartlock) {
    return undefined;
  }

  const params = buildChooserParams(bundle);
  const [hostSku] = getOffDefaultSkus([smartlock], selected);
  if (hostSku) {
    params.set(productDetail.sku, hostSku);
  }

  const setSkus = formatBundleSkusParam(
    getOffDefaultSkus(
      bundle.products.filter((product) => product.id !== smartlock.id),
      selected,
    ),
  );
  if (setSkus) {
    params.set(productDetail.bundleSkus, setSkus);
  }

  return `${smartlock.path}?${params.toString()}`;
}

/**
 * The link of the bundle that opens it with the given variants preselected: its page with
 * `setSkus`, or the chooser, where the Smart Lock is the host and keeps its own `sku`
 * parameter. A default selection yields the same link as `Bundle.getLinkPath()`.
 */
export function buildLinkPathForSelection(
  bundle: Bundle,
  selected: SelectedProductVariants | undefined,
): string | undefined {
  const pagePath = bundle.getLinkablePagePath();
  if (pagePath) {
    const params = new URLSearchParams();
    const setSkus = formatBundleSkusParam(getOffDefaultSkus(bundle.products, selected));
    if (setSkus) {
      params.set(productDetail.bundleSkus, setSkus);
    }

    return withQuery(pagePath, params);
  }

  return buildChooserPathForSelection(bundle, selected);
}
